import React, { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { qualifiersApi, championshipApi, leaguesApi } from '../services/api';

function Championship({ user, onLogout }) {
  const [tab, setTab] = useState('weekly');
  const [weeklyEntries, setWeeklyEntries] = useState(null);
  const [monthlyEntries, setMonthlyEntries] = useState(null);
  const [seats, setSeats] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [weeklyRes, monthlyRes, seatsRes, leaguesRes] = await Promise.all([
        qualifiersApi.getCurrent('weekly').catch(() => ({ data: null })),
        qualifiersApi.getCurrent('monthly').catch(() => ({ data: null })),
        championshipApi.getSeats().catch(() => ({ data: { seats: [] } })),
        leaguesApi.getAll().catch(() => ({ data: { leagues: [] } })),
      ]);
      setWeeklyEntries(weeklyRes.data);
      setMonthlyEntries(monthlyRes.data);
      setSeats(seatsRes.data?.seats || []);
      setLeagues(leaguesRes.data?.leagues || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncWeekly = async () => {
    try {
      const r = await qualifiersApi.syncToPoker3d('weekly');
      alert(`Weekly qualifier ready: ${r.data?.count || 0} players queued for Poker3D sync\n\n${r.data?.note || ''}`);
    } catch (e) { alert(e.message); }
  };

  const handleAwardAttendance = async (leagueId, leagueName) => {
    if (!window.confirm(`Award championship seats to top 20% of ${leagueName} by attendance?`)) return;
    try {
      const r = await championshipApi.awardAttendanceSeats(leagueId);
      alert(`Awarded ${r.data?.awarded || 0} seats from ${leagueName}`);
      fetchData();
    } catch (e) { alert(e.message); }
  };

  const renderEntries = (qData, label) => (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3>{qData?.qualifier?.name || label}</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <span className="badge badge-info">{qData?.count || 0} players</span>
          {tab === 'weekly' && <button className="btn btn-primary btn-sm" onClick={handleSyncWeekly}>↗ Sync to Poker3D</button>}
        </div>
      </div>
      {(!qData?.entries || qData.entries.length === 0) ? (
        <div className="empty-state" style={{ padding: 30 }}>
          <p>No players qualified yet for this {label.toLowerCase()}.</p>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Player</th>
              <th>Phone</th>
              <th>Source Game</th>
              <th>Finish</th>
              <th>Registered</th>
            </tr>
          </thead>
          <tbody>
            {qData.entries.map(e => (
              <tr key={e.id}>
                <td><strong>{e.firstName} {e.lastName}</strong></td>
                <td>{e.phoneNumber || '-'}</td>
                <td>{e.sourceVenue ? `${e.sourceVenue} - ${new Date(e.sourceGameTime).toLocaleDateString()}` : '-'}</td>
                <td>{e.finishPositionAtSource ? `${e.finishPositionAtSource}${['st','nd','rd'][e.finishPositionAtSource-1]||'th'}` : '-'}</td>
                <td>{new Date(e.registeredAt).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="admin-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h2>🏆 Championship Hub</h2>
            <p>Multi-tier qualifier system → Yearly Championship at Bally's</p>
          </div>
          <button className="btn btn-secondary" onClick={fetchData}>↻ Refresh</button>
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="card">
          <div style={{ display: 'flex', gap: 4, padding: '0 16px', borderBottom: '1px solid #e5e7eb' }}>
            {['weekly','monthly','seats','attendance'].map(t => (
              <button key={t} onClick={() => setTab(t)} style={{
                background: 'none', border: 'none',
                color: tab === t ? '#d4a843' : '#666',
                padding: '14px 20px', fontWeight: 700, fontSize: 14,
                cursor: 'pointer', borderBottom: tab === t ? '2px solid #d4a843' : '2px solid transparent',
                textTransform: 'uppercase', letterSpacing: '0.05em'
              }}>
                {t === 'weekly' && '📅 Weekly Qualifier'}
                {t === 'monthly' && '🗓️ Monthly Qualifier'}
                {t === 'seats' && `🎟️ Bally's Seats (${seats.length})`}
                {t === 'attendance' && '📊 Attendance Awards'}
              </button>
            ))}
          </div>

          <div style={{ padding: 20 }}>
            {loading ? <div className="loading"><div className="loading-spinner"></div></div> :
              tab === 'weekly' ? renderEntries(weeklyEntries, 'Weekly Qualifier') :
              tab === 'monthly' ? renderEntries(monthlyEntries, 'Monthly Qualifier') :
              tab === 'seats' ? (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <h3>{new Date().getFullYear()} Championship Seats at Bally's</h3>
                    <span className="badge badge-warning">{seats.length} total</span>
                  </div>
                  {seats.length === 0 ? (
                    <div className="empty-state" style={{ padding: 30 }}>
                      <p>No championship seats awarded yet.</p>
                    </div>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Player</th>
                          <th>Phone</th>
                          <th>Source</th>
                          <th>Awarded</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {seats.map(s => (
                          <tr key={s.id}>
                            <td><strong>{s.firstName} {s.lastName}</strong></td>
                            <td>{s.phone || '-'}</td>
                            <td>
                              <span className="badge badge-info">
                                {s.source === 'weekly_qualifier_top3' ? '🥇 Weekly Top 3' :
                                 s.source === 'monthly_qualifier' ? '🗓️ Monthly' :
                                 s.source === 'big_money_game_winner' ? '💰 Big Money' :
                                 s.source === 'attendance_top_20' ? '📊 Attendance' :
                                 s.source}
                              </span>
                            </td>
                            <td>{new Date(s.awardedAt).toLocaleDateString()}</td>
                            <td>
                              {s.redeemed ? <span className="badge badge-success">✓ Redeemed</span> : <span className="badge badge-secondary">Pending</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              ) : (
                <div>
                  <h3>Award Attendance-Based Seats</h3>
                  <p style={{ color: '#666', marginBottom: 16 }}>Award championship seats to the top 20% of each league by games attended.</p>
                  {leagues.length === 0 ? (
                    <p className="empty-state">No leagues found.</p>
                  ) : (
                    <table className="data-table">
                      <thead>
                        <tr><th>League</th><th>Action</th></tr>
                      </thead>
                      <tbody>
                        {leagues.map(l => (
                          <tr key={l.id}>
                            <td><strong>{l.name}</strong></td>
                            <td>
                              <button className="btn btn-primary btn-sm" onClick={() => handleAwardAttendance(l.id, l.name)}>
                                Award Top 20%
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )
            }
          </div>
        </div>
      </main>
    </div>
  );
}

export default Championship;
